import React from 'react';
import { clsx } from 'clsx';
import { ShieldCheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { PositionDetail } from '@/types/trades.types';
import { Panel, PanelHeader, PanelBody } from '@/components/ui/Panel';
import { Field } from '@/components/ui/Field';
import { formatDecimalParts, formatDecimalString, toDecimalString } from '@/utils/decimal-display';
import { deriveMarginAdequacy } from './analytics-economics';

interface AdequacyCardProps {
  trade: PositionDetail;
}

const HEADER_ICON = (
  <ShieldCheckIcon className="h-[15px] w-[15px] flex-none text-lr-muted" aria-hidden="true" />
);

/**
 * Margin & collateral adequacy (prototype `.card` + coverage block). Reshape of
 * the former MarginAdequacySection — same SHARED `deriveMarginAdequacy` math (so
 * the card and the KPI strip can't drift), restyled to the Panel + Field grid +
 * a coverage bar that is the home of the "Cannot assess" resolution.
 *
 * Behaviour preserved verbatim: no-mark honest empty; cash-collateral →
 * "Cannot assess" (never a fabricated breach, QA-A); booking-based requirement
 * (mark-spike-immune, WR-04); M1 exposure-exceeds-collateral note; M2 direction
 * caption; the margin-call / top-up banner; signed-but-neutral mark-vs-booking.
 *
 * The coverage RATIO and bar width are visual display values (a percentage, not
 * an authoritative money figure), so they read collateral/required through
 * Number() — never a displayed cent (HON-03 applies to money strings).
 */
export function AdequacyCard({ trade }: AdequacyCardProps) {
  const currency = trade.currency;
  const m = deriveMarginAdequacy(trade);

  // LR-576 (feedback п.72): cross-ccy secondary line — the backend settlement
  // twin, honestly marked approximate. Null for same-ccy trades (twin absent).
  // Uppercased comparison + well-formedness gate mirror the canonical
  // hasSettlementDailyReturn / isCrossCurrency pattern (LR-474 r1: a 'usd' vs
  // 'USD' payload must not read as cross-ccy; a malformed string must be
  // rejected rather than rendered as "≈ —").
  const settlementTwin = (converted: string | null | undefined): string | null =>
    converted != null &&
    !formatDecimalParts(converted).isEmpty &&
    !!trade.settlement_currency &&
    trade.settlement_currency.toUpperCase() !== (trade.currency || '').toUpperCase()
      ? `≈ ${formatDecimalString(converted, { currency: trade.settlement_currency, dp: 2 })}`
      : null;
  const joinHint = (...parts: Array<string | null | undefined>): string | undefined => {
    const s = parts.filter(Boolean).join(' · ');
    return s || undefined;
  };
  const isRepoSide = trade.trade_type === 'repo' || trade.trade_type === 'reverse_repo';

  // LR-576 (feedback п.72): repo/reverse short-circuits BEFORE the verdict
  // branch — the customer's AC is unconditional ("repo → Trade Margin Value +
  // cash-pool caption, no fake verdict"), and the backend only hardens the
  // marked_* display twins against a stray repo mark (position.py), NOT
  // latest_mark_price: a stray mark would otherwise flip this card into a
  // fabricated per-trade adequacy view. Repo margin lives in the per-CP cash
  // pool (LR-385/479) on the MtM page.
  if (isRepoSide || m.verdict === 'no_mark') {
    // A never-marked trade shows its BOOKING basis under an honest label instead
    // of a bare empty state (the customer's prescription: Trade Margin Value when
    // never marked). Verdict math (deriveMarginAdequacy) is untouched — no
    // fabricated adequacy without a mark.
    return (
      <Panel>
        <PanelHeader icon={HEADER_ICON} title="Margin & collateral adequacy" />
        <PanelBody>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            <Field
              label="Trade Margin Value"
              size="big"
              empty={trade.contract_value == null}
              emptyLabel="—"
              value={formatDecimalString(trade.contract_value, { currency, dp: 2 })}
              hint={joinHint(
                isRepoSide ? 'booking basis' : 'never marked · booking basis',
                settlementTwin(trade.contract_value_converted),
              )}
            />
          </div>
          <p className="mt-4 max-w-md text-sm text-lr-muted">
            {isRepoSide
              ? 'Repo margin is managed at counterparty level via the cash pool — see Collateral Manager (Cash Pools -> GMRA).'
              : 'No mark recorded yet — collateral adequacy is unavailable until the first mark.'}
          </p>
        </PanelBody>
      </Panel>
    );
  }

  const breach = m.verdict === 'breach';
  const cannotAssess = m.verdict === 'cannot_assess';
  const markDeltaNegative = m.markDelta.startsWith('-');
  // LR-208 r5 L2: when markDelta is empty (e.g. gross_price is null) the
  // formatter returns the em-dash; prefixing it with "+" would render the
  // literal "+—". Drop the sign on the honest-empty case.
  const markDeltaFormatted = formatDecimalString(m.markDelta, { currency, dp: 2 });
  const markDeltaSigned =
    markDeltaFormatted === '—'
      ? markDeltaFormatted
      : markDeltaNegative
        ? markDeltaFormatted
        : `+${markDeltaFormatted}`;

  // Coverage ratio — a DISPLAY percentage (collateral / required), not money.
  // Number() is intentional here (like the chart's bar-height ratios): exact
  // cents don't apply to a coverage %, and it never renders as a cent figure.
  const collateralNum = Number(toDecimalString(m.collateral) ?? NaN);
  const requiredNum = Number(toDecimalString(m.required) ?? NaN);
  const coverage =
    Number.isFinite(collateralNum) && Number.isFinite(requiredNum) && requiredNum !== 0
      ? (collateralNum / requiredNum) * 100
      : null;
  const coveragePct = coverage == null ? '—' : `${coverage.toFixed(1)}%`;
  const barWidth = coverage == null ? 100 : Math.max(2, Math.min(coverage, 100));

  return (
    <Panel>
      <PanelHeader icon={HEADER_ICON} title="Margin & collateral adequacy" meta="as of last mark" />
      <PanelBody>
        {/* M2: collateral-risk direction — breach/excess mean opposite things by side. */}
        <p className="-mt-1 mb-4 text-xs text-lr-muted">
          {m.isLendSide
            ? 'Counterparty posts collateral to us.'
            : 'We post collateral to the counterparty (a shortfall is our top-up obligation).'}
        </p>

        {breach && (
          <div
            className={clsx(
              'mb-4 flex items-start gap-2 rounded-lg border p-4',
              m.isLendSide ? 'border-lr-neg/30 bg-lr-neg-weak' : 'border-amber-200 bg-amber-50',
            )}
          >
            <ExclamationTriangleIcon
              className={clsx('h-5 w-5 shrink-0', m.isLendSide ? 'text-lr-neg' : 'text-amber-600')}
              aria-hidden="true"
            />
            <p className={clsx('text-sm', m.isLendSide ? 'text-lr-neg' : 'text-amber-700')}>
              {m.isLendSide
                ? 'Margin call — counterparty collateral shortfall of '
                : 'Collateral top-up due — we owe '}
              <span className="lr-num font-semibold">
                {formatDecimalString(m.deltaAbs, { currency, dp: 2 })}
              </span>{' '}
              (as of last mark)
            </p>
          </div>
        )}

        {/* LR-576 (feedback п.72): "Current exposure" is removed from this section
            (customer's ask); the grid pairs the BOOKING basis (Trade Margin Value =
            contract_value) with the MARKED basis (Last Marked Margin Value =
            marked_contract_value — strictly the latest mark, NOT closing_cash,
            which can be projected off the booking snapshot). Verdict math
            (deriveMarginAdequacy) is untouched — KpiStrip/StickySummary agree. */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field
            label="Trade Margin Value"
            size="big"
            empty={trade.contract_value == null}
            emptyLabel="—"
            value={formatDecimalString(trade.contract_value, { currency, dp: 2 })}
            hint={joinHint(
              m.isCashCollateral
                ? 'cash posted at booking'
                : `booking basis · incl. ${trade.margin}% margin`,
              settlementTwin(trade.contract_value_converted),
            )}
          />
          <Field
            label="Last Marked Margin Value"
            size="big"
            empty={trade.marked_contract_value == null}
            emptyLabel="—"
            value={formatDecimalString(trade.marked_contract_value, { currency, dp: 2 })}
            // "marked basis", not "as of last mark" — the PanelHeader meta already
            // says that, and duplicating the string breaks its uniqueness (tests).
            hint={joinHint('marked basis', settlementTwin(trade.marked_contract_value_converted))}
          />
          <Field
            label="Coverage ratio"
            size="big"
            empty={coverage == null}
            emptyLabel="—"
            value={coveragePct}
            hint={coverage == null ? 'needs collateral value' : undefined}
          />
        </div>

        {/* Coverage block — the home of the "Cannot assess" resolution. */}
        <div className="mt-5">
          {cannotAssess ? (
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-md bg-lr-warn-weak px-2.5 py-1 text-[12.5px] font-semibold text-lr-warn">
              <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" />
              Adequacy cannot be assessed
            </div>
          ) : (
            <div className="mb-1.5 text-[12.5px] font-semibold">
              {breach ? (
                <span className="text-lr-neg">
                  <span>Shortfall</span>{' '}
                  <span className="lr-num">{formatDecimalString(m.deltaAbs, { currency, dp: 2 })}</span>
                </span>
              ) : (
                <span className="text-lr-pos">
                  <span>Excess</span>{' '}
                  <span className="lr-num">{formatDecimalString(m.delta, { currency, dp: 2 })}</span>
                </span>
              )}
            </div>
          )}
          <div className="h-2.5 overflow-hidden rounded-md border border-lr-border bg-lr-bg">
            <div
              className={clsx(
                'h-full',
                cannotAssess
                  ? 'bg-[repeating-linear-gradient(135deg,#e9b25a,#e9b25a_6px,#f0c479_6px,#f0c479_12px)]'
                  : breach
                    ? 'bg-lr-neg'
                    : 'bg-lr-pos',
              )}
              style={{ width: `${barWidth}%` }}
            />
          </div>
          {/* LR-576 (feedback п.72): non-cash trades — the verdict compares posted
              securities (collateral_value) against the booking requirement, and the
              posted figure no longer sits in the hero grid, so surface both here:
              a verdict computed off an invisible number would be dishonest. */}
          {/* required != null too: with an unknown requirement the sentence would
              read "vs booking requirement —" right above the cannot-assess text
              that already explains the missing basis (the LR-208 r6 dash-in-prose
              class of bug). */}
          {!m.isCashCollateral && m.collateral != null && m.required != null && (
            <p className="mt-2 text-[11.5px] leading-relaxed text-lr-faint">
              Posted collateral{' '}
              <span className="lr-num">{formatDecimalString(m.collateral, { currency, dp: 2 })}</span>{' '}
              vs booking requirement{' '}
              <span className="lr-num">{formatDecimalString(m.required, { currency, dp: 2 })}</span>.
            </p>
          )}
          {cannotAssess && (
            <p className="mt-2 text-[11.5px] leading-relaxed text-lr-faint">
              No collateral value has been received, so the coverage ratio cannot be
              computed
              {/* When the requirement is ALSO unknown (contract_value absent), don't
                  render "against the — requirement" (LR-208 r6 LOW). */}
              {m.required == null ? (
                ' (the requirement basis is also unavailable)'
              ) : (
                <>
                  {' '}against the{' '}
                  <span className="lr-num">
                    {formatDecimalString(m.required, { currency, dp: 2 })}
                  </span>{' '}
                  requirement
                </>
              )}
              . Resolve by posting collateral or refreshing the custody feed.
            </p>
          )}
        </div>

        {/* M1: marked exposure already tops posted collateral, yet the verdict is
            booking-based (WR-04) so it isn't a breach — surface the gap honestly. */}
        {m.exposureExceedsCollateral && m.isLendSide && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            <p className="text-xs text-amber-700">
              Marked exposure exceeds posted collateral — adequacy is measured against the booking
              requirement (mark-spike-immune, WR-04), so this is not flagged as a breach. Re-mark to
              reassess if collateral must track market value.
            </p>
          </div>
        )}

        <div className="mt-4 border-t border-lr-border pt-4">
          <p className="mb-1 text-xs text-lr-muted">Current mark vs booking price</p>
          <p data-testid="mark-delta" className="lr-num text-lg font-semibold text-lr-fg">
            {markDeltaSigned}
          </p>
        </div>
      </PanelBody>
    </Panel>
  );
}
